/**
 * Ticket Status Badge Component
 * Displays a colored badge for ticket status with accessibility support
 */

import { Chip } from '@mui/material'
import { TicketStatus, TicketStatusLabels } from '@api/types/tickets'

interface TicketStatusBadgeProps {
  status: TicketStatus
  size?: 'small' | 'medium'
}

const statusColors: Record<TicketStatus, 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
  [TicketStatus.Pending]: 'default',
  [TicketStatus.Claimed]: 'info',
  [TicketStatus.InProgress]: 'secondary',
  [TicketStatus.AwaitingUser]: 'warning',
  [TicketStatus.Resolved]: 'success',
  [TicketStatus.Closed]: 'default',
  [TicketStatus.Escalated]: 'error',
}

const statusSx: Record<TicketStatus, any> = {
  [TicketStatus.Pending]: { bgcolor: 'grey.500', color: 'white' },
  [TicketStatus.Claimed]: { bgcolor: 'info.main', color: 'white' },
  [TicketStatus.InProgress]: { bgcolor: 'secondary.main', color: 'white' },
  [TicketStatus.AwaitingUser]: { bgcolor: 'warning.main', color: 'white' },
  [TicketStatus.Resolved]: { bgcolor: 'success.main', color: 'white' },
  [TicketStatus.Closed]: { bgcolor: 'grey.700', color: 'white' },
  [TicketStatus.Escalated]: { bgcolor: 'error.main', color: 'white', fontWeight: 'bold' },
}

export function TicketStatusBadge({ status, size = 'small' }: TicketStatusBadgeProps) {
  return (
    <Chip
      label={TicketStatusLabels[status]}
      color={statusColors[status]}
      size={size}
      sx={{
        ...statusSx[status],
        minWidth: 80,
      }}
      aria-label={`Status: ${TicketStatusLabels[status]}`}
    />
  )
}
